import coursesSlice from "./slices/courses";
import assessmentsSlice from "./slices/assessments";
import usersSlice from "./slices/users";
import proctoringSessionsSlice from "./slices/proctoringSessions";
import { useContext, useEffect, useMemo } from "react";
import { RootState } from "./index";
import { PopInContext } from "../context";
import { useSelector } from "react-redux";
import { AnalyzedStatus, FlagSeverity, selectPopInSessions, SortDirection, SortField } from "./selectors";
import { createSelector } from "@reduxjs/toolkit";
import { getPopInSessionFlagCounts } from "./slices/popInSessions";

export const { useGetCoursesQuery, useGetCourseQuery, useGetVerityCourseQuery } = coursesSlice;
export const { useGetAssessmentQuery } = assessmentsSlice;
export const { useGetUserQuery } = usersSlice;
export const { useGetProctoringSessionByLiveUserSessionIdQuery, useGetProctoringSessionConfigurationQuery } =
	proctoringSessionsSlice;

export type PopInSessionsQueryParams = { instituteIds?: string[]; courseIds?: string[] };

export function useGetPopInSessionsQuery({
	instituteIds,
	courseIds,
	studentName,
	severities,
	analyzedStatuses,
	sortBy,
	sortDirection = "asc",
	page = 0,
	size = 5,
}: {
	instituteIds?: string[];
	courseIds?: string[];
	studentName?: string;
	severities?: FlagSeverity[];
	analyzedStatuses?: AnalyzedStatus[];
	sortBy?: SortField;
	sortDirection?: SortDirection;
	size?: number;
	page?: number;
}) {
	const { messageChannelManager } = useContext(PopInContext);

	useEffect(() => {
		const { state } = messageChannelManager;
		if (state === "new") {
			messageChannelManager.init({ instituteIds, courseIds });
		} else if (state !== "closed") {
			messageChannelManager.reinit({ instituteIds, courseIds });
		}
	}, [instituteIds, courseIds]);

	return useSelector((state: RootState) =>
		selectPopInSessions(state, {
			studentName,
			severities,
			analyzedStatuses,
			sortBy,
			sortDirection,
			size,
			page,
		}),
	);
}

export function useSelectPopInSessionFlagCounts(sessionId: string) {
	const selector = useMemo(
		() =>
			createSelector(
				(state: RootState) => state.popInSessions.sessions,
				(sessions) => {
					const session = sessions.entities[sessionId];
					if (session) {
						return getPopInSessionFlagCounts(session);
					}
					return {
						severe: 0,
						major: 0,
						minor: 0,
					};
				},
			),
		[],
	);
	return useSelector(selector);
}
